import i18n from "@/i18n";
/**
 * EmployeeProfileSheet — Prompt 10C §5-§34.
 *
 * Right-side shadcn Sheet showing the current Employee/Employment/
 * Terms plus a read-only Employee Mobile status and the full
 * Employment Terms History for the tenant. Location-contextual per
 * Prompt 9C: the current MiseOne restaurantId is the authorized
 * scope. Prompt 10C is READ-ONLY — no edit/change/deactivate
 * controls and no writes to any endpoint.
 */
import { useCallback, useEffect, useState } from "react";
import {
    Sheet, SheetContent, SheetHeader, SheetTitle,
} from "@/components/ui/sheet";
import { Badge } from "@/components/ui/badge";
import { Button } from "@/components/ui/button";
import { Skeleton } from "@/components/ui/skeleton";
import { useApp } from "@/context/AppContext";
import AbsenceManager from
    "@/components/workforce/AbsenceManager";
import { api } from "@/lib/api";
import EmployeeMobileAccessDialog from
    "@/components/workforce/EmployeeMobileAccessDialog";
import { PlanningComplianceSummary }
    from "@/components/workforce/PlanningComplianceSummary";
import VVCard from "@/components/workforce/VVCard";
import VuosilomaCard from "@/components/workforce/VuosilomaCard";
import ChangeRestaurantDialog from
    "@/components/workforce/ChangeRestaurantDialog";
import VVSystemRevisionDialog from
    "@/components/workforce/VVSystemRevisionDialog";
import { canManageAbsences } from "@/components/workforce/absenceShared";
import {
    browserLocalYMD, contractTypeLabel, formatDurationMinutes,
    formatYMDDate, workingTimeModelLabel, tesFamilyLabel,
    payrollIncludedLabel, employeeStatusLabel,
    employeeMobileStatusLabel, vvSystemLabel, VV_MANAGE_ROLES,
} from "./workforceHelpers";

// ── Pure formatting helpers exported for tests ────────────────
export function formatContractLine({
    contract_type, contract_hours_minutes,
}) {
    return `${contractTypeLabel(contract_type)} · `
        + `${formatDurationMinutes(contract_hours_minutes)} / 3 weeks`;
}

export function formatEffectiveRange({ effective_from, effective_to }) {
    const from = formatYMDDate(effective_from);
    const to = effective_to ? formatYMDDate(effective_to) : "—";
    return `${from} – ${to}`;
}

export function resolveHistoricalLocationName(restaurants, mainRestaurantId) {
    if (!Array.isArray(restaurants)) return "—";
    const r = restaurants.find((x) => x?.id === mainRestaurantId);
    return r?.name || "—";
}

// Prompt 16A §18 — HOURS/MINUTES balance display. Internal storage
// remains INTEGER MINUTES (§7); this helper only formats. Never
// exposes raw minutes as the primary display.
export function formatBalanceMinutes(minutes) {
    const n = Number.isFinite(minutes) ? Math.trunc(minutes) : 0;
    if (n === 0) return "0 h";
    const sign = n < 0 ? "−" : "+";
    const abs = Math.abs(n);
    const h = Math.trunc(abs / 60);
    const m = abs % 60;
    if (h === 0) return `${sign}${m} min`;
    if (m === 0) return `${sign}${h} h`;
    return `${sign}${h} h ${m} min`;
}

function Field({ label, value, testId }) {
    return (
        <div className="text-sm" data-testid={testId}>
            <div className="text-xs text-neutral-500">{label}</div>
            <div className="text-neutral-900">{value}</div>
        </div>
    );
}

export default function EmployeeProfileSheet({
    open, onOpenChange, employeeId, onEdit, reloadKey,
}) {
    const { restaurantId, currentRestaurant, restaurants, user } = useApp();
    const restaurantName = currentRestaurant?.name || "";

    const [profile, setProfile] = useState(null);
    const [history, setHistory] = useState(null);
    // Prompt 16A §18 — additive Averaging summary. Loaded ONLY when
    // the current terms `working_time_model === "AVERAGING"`.
    const [averaging, setAveraging] = useState(null);
    const [loading, setLoading] = useState(false);
    const [error, setError] = useState(false);
    // Prompt 15B §8 — one-time Mobile access dialog. Never persists.
    const [mobileAccessOpen, setMobileAccessOpen] = useState(false);
    // TES-B2C1B1 — effective-dated VV system revision dialog.
    const [vvRevisionOpen, setVvRevisionOpen] = useState(false);
    // VUOSILOMA-V1 — refresh the annual-leave card after absence edits.
    const [vlReload, setVlReload] = useState(0);
    const canManageVV = !!(user && VV_MANAGE_ROLES.has(user.role));
    // INTERNAL TRANSFER — backend needs scope over both restaurants;
    // only Corporate accounts have it (Managers are single-restaurant).
    const [transferOpen, setTransferOpen] = useState(false);
    const canTransfer = user?.role === "corporate" && (restaurants || []).length > 1;

    const load = useCallback(async () => {
        if (!open || !employeeId || !restaurantId) return;
        setLoading(true);
        setError(false);
        // Prompt 10C §7 · §27 — clear before requesting.
        setProfile(null);
        setHistory(null);
        setAveraging(null);
        try {
            const [prof, hist] = await Promise.all([
                api.get(`/workforce/employees/${employeeId}`, {
                    params: {
                        restaurant_id: restaurantId,
                        as_of: browserLocalYMD(),
                    },
                }),
                api.get(`/workforce/employees/${employeeId}/employment-terms`, {
                    params: { restaurant_id: restaurantId },
                }),
            ]);
            setProfile(prof.data);
            setHistory(hist.data);
            // Prompt 16A §18 — only fetch Averaging when the current
            // terms carry AVERAGING. STANDARD employees see NO
            // unnecessary Averaging UI (§18).
            const wtm = prof.data?.current_terms?.working_time_model;
            if (wtm === "AVERAGING") {
                try {
                    const { data: avgData } = await api.get(
                        `/workforce/employees/${employeeId}/averaging`,
                        { params: { restaurant_id: restaurantId } },
                    );
                    setAveraging(avgData);
                } catch (_avgErr) {
                    // Averaging block is additive; a lookup failure
                    // must not break the profile view.
                    setAveraging(null);
                }
            }
        } catch (_err) {
            setError(true);
        } finally {
            setLoading(false);
        }
    }, [open, employeeId, restaurantId]);

    // Reload every time the panel opens for a different employee,
    // Location changes while open (the parent also closes the panel on
    // Location change per Prompt 10C §29), OR the parent bumps
    // `reloadKey` after a successful mutation (Prompt 12L-2C §12).
    useEffect(() => { load(); }, [load, reloadKey]);

    // Prompt 10C §33 — reset local state when the sheet closes.
    useEffect(() => {
        if (!open) {
            setProfile(null);
            setHistory(null);
            setAveraging(null);
            setError(false);
            setLoading(false);
        }
    }, [open]);

    const emp = profile?.employee;
    const employment = profile?.employment;
    const terms = profile?.current_terms;
    const mobileStatus = profile?.employee_mobile?.status;

    const title = emp ? `${emp.first_name} ${emp.last_name}` : "";

    return (
        <Sheet open={open} onOpenChange={onOpenChange}>
            <SheetContent
                side="right"
                data-testid="employee-profile-sheet"
                className="w-full sm:max-w-lg overflow-y-auto"
            >
                <SheetHeader>
                    <SheetTitle data-testid="employee-profile-title">
                        {title || "\u00A0"}
                    </SheetTitle>
                    {emp && (
                        <div className="flex items-center justify-between
                                        gap-3 mt-1">
                            <Badge
                                variant="secondary"
                                data-testid="employee-profile-status"
                                className="w-fit"
                            >
                                {employeeStatusLabel(emp.status)}
                            </Badge>
                            {/* Prompt 12L-2C §5 — Edit intent. No
                                destructive-action controls. */}
                            {typeof onEdit === "function" && canManageAbsences(user) && (
                                <Button
                                    variant="outline"
                                    size="sm"
                                    data-testid="employee-profile-edit"
                                    onClick={() => onEdit(emp)}
                                >
                                    {i18n.t("common.edit", "Edit")}
                                </Button>
                            )}
                        </div>
                    )}
                </SheetHeader>

                {loading && (
                    <div className="mt-4 space-y-3"
                            data-testid="employee-profile-loading">
                        {[0, 1, 2, 3].map((i) => (
                            <Skeleton key={i} className="h-8 w-full" />
                        ))}
                    </div>
                )}

                {error && !loading && (
                    <div
                        data-testid="employee-profile-error"
                        className="mt-6 rounded-md border border-neutral-200
                                    bg-white p-6 text-center space-y-3"
                    >
                        <p className="text-sm text-neutral-700">
                            {i18n.t("wf.employeeProfileSheet.couldNotLoadEmployee", "Could not load employee.")}
                        </p>
                        <Button
                            variant="outline"
                            data-testid="employee-profile-retry"
                            onClick={load}
                        >
                            {i18n.t("wf.employeeProfileSheet.retry", "Retry")}
                        </Button>
                    </div>
                )}

                {!loading && !error && profile && (
                    <div className="mt-4 space-y-6">
                        {/* Employment */}
                        <section data-testid="employee-profile-employment">
                            <h3 className="text-sm font-semibold
                                            text-neutral-700 mb-3">
                                {i18n.t("wf.employeeProfileSheet.employment", "Employment")}
                            </h3>
                            <div className="grid grid-cols-2 gap-4">
                                <Field label={i18n.t("wf.employeeProfileSheet.employeeNumber", "Employee Number")}
                                        value={profile?.employee
                                            ?.employee_number || "—"}
                                        testId="ep-employee-number" />
                                <div>
                                    <Field label={i18n.t("wf.employeeProfileSheet.mainLocation", "Main Location")}
                                            value={restaurantName || "—"}
                                            testId="ep-main-location" />
                                    {terms && canTransfer && (
                                        <Button variant="outline" size="sm"
                                            className="mt-1"
                                            data-testid="ep-change-restaurant"
                                            onClick={() => setTransferOpen(true)}>
                                            {i18n.t("wf.employeeProfileSheet.changeRestaurant", "Change restaurant")}
                                        </Button>
                                    )}
                                </div>
                                <Field label={i18n.t("wf.employeeProfileSheet.mainRole", "Main Role")}
                                        value={terms?.main_role || "—"}
                                        testId="ep-main-role" />
                                <Field label={i18n.t("wf.employeeProfileSheet.contract", "Contract")}
                                        value={terms
                                            ? formatContractLine(terms)
                                            : "—"}
                                        testId="ep-contract" />
                                <Field label={i18n.t("wf.employeeProfileSheet.workingTimeModel", "Working Time Model")}
                                        value={workingTimeModelLabel(
                                            terms?.working_time_model)
                                            || "—"}
                                        testId="ep-wtm" />
                                <Field label={i18n.t("wf.employeeProfileSheet.tes", "TES")}
                                        value={tesFamilyLabel(
                                            terms?.tes_family) || "—"}
                                        testId="ep-tes" />
                                <Field label={i18n.t("wf.employeeProfileSheet.payrollIncluded", "Payroll Included")}
                                        value={payrollIncludedLabel(
                                            terms?.payroll_included)}
                                        testId="ep-payroll" />
                                <div className="text-sm col-span-2
                                                flex items-end
                                                justify-between gap-3"
                                    data-testid="ep-vv-system">
                                    <div>
                                        <div className="text-xs
                                                        text-neutral-500">
                                            {i18n.t("wf.employeeProfileSheet.vvSystem", "VV system")}
                                        </div>
                                        <div data-testid="ep-vv-system-value"
                                            className={terms?.vv_system
                                                ? "text-neutral-900"
                                                : "text-amber-700"}>
                                            {terms
                                                ? vvSystemLabel(terms.vv_system)
                                                : "—"}
                                        </div>
                                    </div>
                                    {terms && canManageVV && (
                                        <Button variant="outline" size="sm"
                                            data-testid="ep-vv-system-action"
                                            onClick={() =>
                                                setVvRevisionOpen(true)}>
                                            {terms.vv_system
                                                ? i18n.t("wf.employeeProfileSheet.changeVvSystem", "Change VV system")
                                                : i18n.t("wf.employeeProfileSheet.setVvSystem", "Set VV system")}
                                        </Button>
                                    )}
                                </div>
                                <Field label={i18n.t("wf.employeeProfileSheet.startDate", "Start Date")}
                                        value={formatYMDDate(
                                            employment?.start_date)}
                                        testId="ep-start-date" />
                                <Field label={i18n.t("wf.employeeProfileSheet.endDate", "End Date")}
                                        value={employment?.end_date
                                            ? formatYMDDate(
                                                employment.end_date)
                                            : "—"}
                                        testId="ep-end-date" />
                            </div>
                        </section>

                        {/* Employee Mobile */}
                        <section data-testid="employee-profile-mobile">
                            <h3 className="text-sm font-semibold
                                            text-neutral-700 mb-3">
                                {i18n.t("wf.employeeProfileSheet.employeeMobile", "Employee Mobile")}
                            </h3>
                            <div className="flex items-center justify-between
                                            gap-3">
                                <Field
                                    label={i18n.t("common.status", "Status")}
                                    value={employeeMobileStatusLabel(
                                        mobileStatus)}
                                    testId="ep-mobile-status"
                                />
                                {/* Prompt 15B §8 — compact Mobile access
                                    action reusing existing MiseOne
                                    outline-button style. */}
                                {canManageAbsences(user) && (
                                <Button
                                    variant="outline"
                                    size="sm"
                                    data-testid="employee-profile-mobile-access"
                                    onClick={() => setMobileAccessOpen(true)}
                                >
                                    {i18n.t("wf.employeeProfileSheet.mobileAccess", "Mobile access")}
                                </Button>
                                )}
                            </div>
                        </section>

                        {/* Prompt 16A §18 — Averaging summary. Rendered
                            only for AVERAGING employees; STANDARD
                            employees never see this section. Read-only:
                            current cycle range + balance formatted in
                            hours/minutes (NEVER raw minutes) + status.
                            "Set up cycle" is the minimal cycle-setup
                            action when no cycle exists (§18). */}
                        {terms?.working_time_model === "AVERAGING" && (
                            <section data-testid="employee-profile-averaging">
                                <h3 className="text-sm font-semibold
                                                text-neutral-700 mb-3">
                                    {i18n.t("wf.employeeProfileSheet.averaging", "Averaging")}
                                </h3>
                                {averaging?.current_cycle ? (
                                    <div className="grid grid-cols-2 gap-4">
                                        <Field
                                            label={i18n.t("wf.employeeProfileSheet.currentCycle", "Current cycle")}
                                            value={formatEffectiveRange({
                                                effective_from:
                                                    averaging.current_cycle
                                                        .start_date,
                                                effective_to:
                                                    averaging.current_cycle
                                                        .end_date,
                                            })}
                                            testId="ep-averaging-cycle-range"
                                        />
                                        <Field
                                            label={i18n.t("wf.employeeProfileSheet.balance", "Balance")}
                                            value={formatBalanceMinutes(
                                                averaging.balance_minutes)}
                                            testId="ep-averaging-balance"
                                        />
                                        {averaging.balance_status
                                            && averaging.balance_status !== "CONFIRMED" && (
                                            <Field
                                                label={i18n.t("wf.employeeProfileSheet.balanceStatus", "Balance status")}
                                                value={averaging.balance_status === "PENDING"
                                                    ? i18n.t("wf.employeeProfileSheet.pendingSomeEndedPeriodsAreIncomplete", "Pending — some ended periods are incomplete or reopened")
                                                    : i18n.t("wf.employeeProfileSheet.pendingUpdateHoursChangedSinceThe", "Pending update — hours changed since the last update")}
                                                testId="ep-averaging-balance-status"
                                            />
                                        )}
                                        <Field
                                            label={i18n.t("common.status", "Status")}
                                            value={averaging.current_cycle
                                                .status === "OPEN"
                                                ? i18n.t("wf.employeeProfileSheet.open", "Open") : i18n.t("wf.employeeProfileSheet.closed", "Closed")}
                                            testId="ep-averaging-status"
                                        />
                                        {/* Prompt 16B-1 §19 — additive
                                            "Reviewed n / m periods" line
                                            when derived data is present.
                                            No raw minutes shown. */}
                                        {averaging.derived && (
                                            <Field
                                                label={i18n.t("wf.employeeProfileSheet.reviewed", "Reviewed")}
                                                value={
                                                    `${averaging.derived
                                                        .completed_period_count}`
                                                    + ` / `
                                                    + `${averaging.derived
                                                        .period_count} periods`}
                                                testId={"ep-averaging-"
                                                    + "reviewed-periods"}
                                            />
                                        )}
                                    </div>
                                ) : (
                                    <div className="flex items-center
                                                    justify-between gap-3">
                                        <p className="text-sm text-neutral-500"
                                            data-testid="ep-averaging-none">
                                            {i18n.t("wf.employeeProfileSheet.noCurrentCycle", "No current cycle.")}
                                        </p>
                                        <Button
                                            variant="outline"
                                            size="sm"
                                            data-testid="ep-averaging-setup"
                                            disabled
                                        >
                                            {i18n.t("wf.employeeProfileSheet.setUpCycle", "Set up cycle")}
                                        </Button>
                                    </div>
                                )}
                                {/* Prompt 16B-2B2 §3 · §4 — compact
                                    planning compliance surface. Only
                                    rendered when the backend supplied
                                    a planning_compliance object; the
                                    TES12_1_STANDARD scheme guard is
                                    already enforced server-side. */}
                                {averaging?.planning_compliance && (
                                    <div className="mt-3">
                                        <PlanningComplianceSummary
                                            planningCompliance={
                                                averaging.planning_compliance}
                                        />
                                    </div>
                                )}
                            </section>
                        )}

                        {/* TES-B2C1B1 — VV / Vuosivapaa card. */}
                        <VVCard employeeId={employeeId}
                            canManage={canManageVV}
                            canManageOpening={canManageAbsences(user)}
                            hasTerms={!!terms}
                            onSetVvSystem={() => setVvRevisionOpen(true)} />

                        {/* VUOSILOMA-V1 — annual-leave ledger (separate from VV). */}
                        {terms && (
                            <VuosilomaCard employeeId={employeeId}
                                restaurantId={restaurantId}
                                canManageFacts={canManageAbsences(user)}
                                reloadKey={vlReload} />
                        )}

                        {/* Prompt 18B1 §2 · §5 · §6 · §7 —
                            Employee Profile Absences card. Manager
                            Create / Edit / Cancel wired to the canonical /api/workforce/absences CRUD
                            endpoints; role gate mirrors the backend
                            (§10). Read-only for unauthorized roles.
                        */}
                        {/* PAYROLL-FINAL-B7 — the profile's current
                            Terms block carries no employment_id; the
                            canonical id lives on `profile.employment`. */}
                        {employeeId && employment?.employment_id && (
                            <AbsenceManager
                                restaurantId={restaurantId}
                                user={user}
                                employeeId={employeeId}
                                employmentId={employment.employment_id}
                                onChanged={() => setVlReload((n) => n + 1)}
                            />
                        )}

                        {/* Employment Terms History */}
                        <section data-testid="employee-profile-history">
                            <h3 className="text-sm font-semibold
                                            text-neutral-700 mb-3">
                                {i18n.t("wf.employeeProfileSheet.employmentTermsHistory", "Employment Terms History")}
                            </h3>
                            {(history?.history?.length ?? 0) === 0 && (
                                <p className="text-sm text-neutral-500"
                                    data-testid="ep-history-empty">
                                    —
                                </p>
                            )}
                            <div className="space-y-3">
                                {history?.history?.map((row, idx) => (
                                    <div
                                        key={idx}
                                        data-testid={`ep-history-row-${idx}`}
                                        className="rounded-md border
                                                    border-neutral-200
                                                    bg-white p-3
                                                    grid grid-cols-2 gap-3"
                                    >
                                        <Field
                                            label={i18n.t("wf.employeeProfileSheet.effective", "Effective")}
                                            value={formatEffectiveRange(row)}
                                            testId={`ep-history-effective-${idx}`}
                                        />
                                        <Field
                                            label={i18n.t("wf.employeeProfileSheet.mainLocation", "Main Location")}
                                            value={resolveHistoricalLocationName(
                                                restaurants,
                                                row.main_restaurant_id)}
                                            testId={`ep-history-location-${idx}`}
                                        />
                                        <Field
                                            label={i18n.t("wf.employeeProfileSheet.mainRole", "Main Role")}
                                            value={row.main_role || "—"}
                                            testId={`ep-history-role-${idx}`}
                                        />
                                        <Field
                                            label={i18n.t("wf.employeeProfileSheet.contract", "Contract")}
                                            value={formatContractLine(row)}
                                            testId={`ep-history-contract-${idx}`}
                                        />
                                        <Field
                                            label={i18n.t("wf.employeeProfileSheet.workingTimeModel", "Working Time Model")}
                                            value={workingTimeModelLabel(
                                                row.working_time_model)
                                                || "—"}
                                            testId={`ep-history-wtm-${idx}`}
                                        />
                                        <Field
                                            label={i18n.t("wf.employeeProfileSheet.tes", "TES")}
                                            value={tesFamilyLabel(
                                                row.tes_family) || "—"}
                                            testId={`ep-history-tes-${idx}`}
                                        />
                                        <Field
                                            label={i18n.t("wf.employeeProfileSheet.payrollIncluded", "Payroll Included")}
                                            value={payrollIncludedLabel(
                                                row.payroll_included)}
                                            testId={`ep-history-payroll-${idx}`}
                                        />
                                        <Field
                                            label={i18n.t("wf.employeeProfileSheet.vvSystem", "VV system")}
                                            value={vvSystemLabel(
                                                row.vv_system)}
                                            testId={`ep-history-vv-${idx}`}
                                        />
                                    </div>
                                ))}
                            </div>
                        </section>
                    </div>
                )}
            </SheetContent>
            <VVSystemRevisionDialog
                open={vvRevisionOpen}
                onOpenChange={setVvRevisionOpen}
                employeeId={employeeId}
                restaurantId={restaurantId}
                currentVvSystem={terms?.vv_system || ""}
                onSaved={load}
            />
            <ChangeRestaurantDialog
                open={transferOpen}
                onOpenChange={setTransferOpen}
                employeeId={employeeId}
                restaurantId={restaurantId}
                restaurants={restaurants}
                onSaved={() => onOpenChange(false)}
            />
            <EmployeeMobileAccessDialog
                open={mobileAccessOpen}
                onOpenChange={setMobileAccessOpen}
                employee={emp}
                restaurantId={restaurantId}
            />
        </Sheet>
    );
}
